import { RegionShared } from "@1games-ru/region-shared";
import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import formatPrice from "@/core/helpers/formatPrice";
import { ArrowDownInCircleIcon, Bag3Icon, Food2Icon, FridgeIcon, PlusIcon } from "@/shared/icons";
import { Button, KeyActionPanel } from "@/shared/ui";
import { KeyAction } from "@/shared/ui/KeyActionPanel";
import { FRIDGE_CONTAINER } from "@/domains/estate/constants/constants";
import minutesUntilTimer from "@/domains/estate/helpers/minutesUntilTimer";
import useFridgeHandlers from "@/domains/estate/hooks/handlers/useFridgeHandlers";
import useFridgeDelivery from "@/domains/estate/hooks/useFridgeDelivery";
import useShowFridge from "@/domains/estate/hooks/useShowFridge";
import EstateFridgeBody from "@/domains/estate/components/fridge/EstateFridgeBody";
import EstateFridgeBuyImprovementModal from "@/domains/estate/components/fridge/EstateFridgeBuyImprovementModal";
import EstateFridgeCard from "@/domains/estate/components/fridge/EstateFridgeCard";
import EstateFridgeDeliveryModal from "@/domains/estate/components/fridge/EstateFridgeDeliveryModal";
import EstateFridgeGoodsWrapper from "@/domains/estate/components/fridge/EstateFridgeGoodsWrapper";
import EstateFridgeHeader from "@/domains/estate/components/fridge/EstateFridgeHeader";
import EstateFridgeWrapper from "@/domains/estate/components/fridge/EstateFridgeWrapper";
import KeyCode from "@/assets/constants/key-code";

export default function EstateFridgeContainer() {
  const { t } = useTranslation("estate");
  const { fridgeInfo } = useShowFridge();
  const [fridgeState, setFridgeState] = useState<
    typeof RegionShared.Backend.Responses.FridgeGetFridgeResponse | null
  >(null);
  const [openImproveModal, setOpenImproveModal] = useState(false);
  const {
    openDeliveryModal,
    setOpenDeliveryModal,
    productsForDelivery,
    productsValues,
    setProductsForDelivery,
    methodPaymnetDelivery,
    setMethodPaymentDelivery,
    paymentMethods,
    isCreateOrderDelivery,
    onCreateDelivery,
    priceDelivery,
  } = useFridgeDelivery(fridgeState);
  const { upgrade, restock, getFridge, craftItem, isUpgrading, isRestocking, resetFlags } =
    useFridgeHandlers();
  const [craftingItemIds, setCraftingItemIds] = useState<number[]>([]);

  const getCurrFridge = useCallback(async () => {
    if (!fridgeInfo) return null;

    const response = await getFridge({ estateId: fridgeInfo.ownerId });

    setFridgeState(response);

    return response;
  }, [fridgeInfo, getFridge]);

  useEffect(() => {
    getCurrFridge();
  }, [getCurrFridge]);

  const actions = useMemo(
    () =>
      new Map<KeyCode, KeyAction>([
        [
          KeyCode.ESCAPE,
          {
            label: t("estate.fridge.close"),
            keyName: "ESC",
            action: () => {},
            color: "red",
            onClick: () => {},
          },
        ],
      ]),
    [t],
  );

  const upgradeFridge = useCallback(async () => {
    if (!fridgeState) return null;
    const response = await upgrade({ id: fridgeState.id, paymentMethod: PaymentType.Card });
    if (response) {
      await getCurrFridge();
      setOpenImproveModal(false);
    }
    return response;
  }, [fridgeState, getCurrFridge, upgrade]);

  const craft = useCallback(
    async (data: typeof RegionShared.Backend.Requests.FridgeCraftItemRequest) => {
      setCraftingItemIds((state) => [...state, data.itemId]);
      const response = await craftItem(data);
      if (response) {
        await getCurrFridge();
      }
      setCraftingItemIds((state) => state.filter((item) => item !== data.itemId));
    },
    [craftItem, getCurrFridge],
  );

  const restockFridge = useCallback(async () => {
    if (!fridgeState) return null;
    const response = await restock({ id: fridgeState.id });
    if (response) {
      await getCurrFridge();
    }
    return response;
  }, [fridgeState, getCurrFridge, restock]);

  useEffect(() => {
    // после fridgeHide надо отчистить стейт
    if (!fridgeInfo) {
      setFridgeState(null);
      resetFlags();
    }
  }, [fridgeInfo, resetFlags]);

  if (fridgeInfo === null) return null;

  return (
    <EstateFridgeWrapper>
      <EstateFridgeBody>
        <EstateFridgeHeader onClose={() => GameMpProvider.closeCurrentInterface()} />
        {!fridgeState ? (
          <div className="flex flex-col items-center justify-center size-full">
            <div className="spinner" />
          </div>
        ) : (
          <div className="relative flex flex-col grow" id={FRIDGE_CONTAINER}>
            {openImproveModal && (
              <EstateFridgeBuyImprovementModal
                capacity={fridgeState.upgradeCapacity + fridgeState.capacity}
                price={fridgeState.upgradePrice}
                onClose={() => setOpenImproveModal(false)}
                onConfirm={upgradeFridge}
                isConfirming={isUpgrading}
              />
            )}
            {openDeliveryModal && (
              <EstateFridgeDeliveryModal
                onClose={() => setOpenDeliveryModal(false)}
                onPay={async () => {
                  const isCreate = await onCreateDelivery();
                  if (isCreate) await getCurrFridge();
                }}
                isPending={isCreateOrderDelivery}
                sliderValue={productsForDelivery}
                sliderValues={productsValues}
                onChangeSliderValue={setProductsForDelivery}
                paymentMethod={methodPaymnetDelivery}
                onChangePaymentMethod={setMethodPaymentDelivery}
                totalPrice={formatPrice(priceDelivery)}
                paymentMethods={paymentMethods}
              />
            )}
            <div className="flex items-center pl-8.5 pr-7.5 pb-3.5">
              <p className="text-18 font-bold leading-none">{t("estate.fridge.select")}</p>
              <div className="flex items-center ml-auto">
                <Food2Icon className="size-5 text-accent-solid" />
                <p className="ml-1.5 mr-3 text-11 font-bold uppercase leading-none">
                  {t("estate.fridge.stockOfProducts")}:{" "}
                  <span className="ml-0.5 text-14 leading-none text-accent-solid">
                    {fridgeState.materials}
                  </span>
                </p>
                <Button className="size-8.5 p-0" onClick={restockFridge} disabled={isRestocking}>
                  {isRestocking ? <div className="spinner" /> : <PlusIcon className="size-3" />}
                </Button>
              </div>
            </div>
            <EstateFridgeGoodsWrapper>
              {fridgeState.items.map((item) => {
                const isCrafting = craftingItemIds.includes(item.itemId);
                return (
                  <EstateFridgeCard
                    key={item.id}
                    title={item.item.name}
                    description={item.item.description}
                    weight={0}
                    src={`https://cdn.region.game/images/inventory/items/${item.item.id}.webp`}
                  >
                    <div className="flex gap-2">
                      <Button
                        className="gap-1 w-[4.6875rem] h-[2.625rem] p-0 bg-gradient-to-r from-accent-solid/30 to-accent-muted/30 pointer-events-none"
                        onClick={() => {}}
                        variant="surface"
                      >
                        <Food2Icon className="size-4.5 text-accent-solid" />
                        {item.materials}
                      </Button>
                      <Button
                        className="h-[2.625rem] gap-1.5 grow p-0"
                        onClick={() =>
                          craft({
                            amount: 1,
                            id: fridgeState.id,
                            itemId: item.id,
                          })
                        }
                        disabled={isCrafting}
                        loading={isCrafting}
                      >
                        <ArrowDownInCircleIcon className="size-5" />
                        {t("estate.fridge.toTake")}
                      </Button>
                    </div>
                  </EstateFridgeCard>
                );
              })}
            </EstateFridgeGoodsWrapper>
            <div className="relative flex items-center px-7.5 pt-3 pb-8.5 bg-dark-900">
              <div className="absolute z-2 -top-12 left-0 w-full h-12 bg-gradient-to-t from-dark-900 to-transparent" />
              <Food2Icon className="size-6.5 mr-2.5 text-white/20" />
              <div className="flex flex-col gap-1">
                <p className="text-16 font-bold leading-none">
                  {t("estate.fridge.capacity")}:{" "}
                  <span className="ml-0.5 text-accent-solid">{fridgeState.capacity}</span>
                </p>
                <p className="text-15 font-medium text-white/50 leading-none">
                  {t("estate.fridge.maxProducts")}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                {!fridgeState.isImproved && (
                  <Button className="h-[2.75rem]" onClick={() => setOpenImproveModal(true)}>
                    <FridgeIcon className="size-4.5 mr-1.5" />
                    {t("estate.fridge.buyImprovement")}
                  </Button>
                )}
                <Button
                  className="h-[2.75rem]"
                  onClick={() => setOpenDeliveryModal(true)}
                  disabled={!!fridgeState?.nextDeliveryAt || isCreateOrderDelivery}
                >
                  <Bag3Icon className="size-4.5 mr-1.5" />
                  {fridgeState?.nextDeliveryAt ? (
                    <>
                      {t("estate.fridge.deliveryUntil")}
                      {" - "}
                      {minutesUntilTimer(new Date(fridgeState.nextDeliveryAt))}
                    </>
                  ) : (
                    t("estate.fridge.orderDelivery")
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </EstateFridgeBody>
      <KeyActionPanel
        name="tablet"
        actions={actions}
        className="fixed bottom-[--boundary-offset-y] left-[50%] -translate-x-[50%] z-[51]"
      />
    </EstateFridgeWrapper>
  );
}
